/**
 * Zod schema for the Financial Needs Assessment profile form.
 *
 * Mirrors the server-side Pydantic schema at fna_app/schemas/fna_input.py
 * field-for-field, including bounds and the retireAge > age cross-field rule.
 * Keeping the two in sync means anything the client accepts will also clear
 * the backend validator.
 */

import { z } from 'zod'

/**
 * Treat empty strings and nulls as "no value provided" so that
 * `z.coerce.number()` doesn't quietly coerce them into `0`. Required fields
 * then fail with a sensible "Required" message instead of "Must be 18+".
 */
const emptyToUndefined = (value: unknown): unknown => {
  if (value === '' || value === null) return undefined
  return value
}

/**
 * Wrap an optional yes/no enum so that the placeholder option (value="")
 * round-trips as `undefined` instead of failing the enum check.
 */
const optionalYesNo = z.preprocess(
  emptyToUndefined,
  z.enum(['yes', 'no']).optional(),
)

/**
 * Optional more/same/less enum (retirement-income-needs question). Same
 * empty-string-friendly pattern as `optionalYesNo`.
 */
const optionalMoreSameLess = z.preprocess(
  emptyToUndefined,
  z.enum(['more', 'same', 'less']).optional(),
)

/** Optional bounded free-text field that round-trips empty inputs as `undefined`. */
const optionalShortText = z
  .string()
  .max(200)
  .optional()
  .transform((value) => (value === '' ? undefined : value))

/** Optional non-negative numeric field, tolerant of empty form inputs. */
const optionalNonNegativeNumber = z.preprocess(
  emptyToUndefined,
  z.coerce.number().nonnegative('Cannot be negative').optional(),
)

/** Optional non-negative numeric field with a default of 0 (matches Pydantic defaults). */
const optionalNonNegativeNumberDefault0 = z.preprocess(
  emptyToUndefined,
  z.coerce.number().nonnegative('Cannot be negative').default(0),
)

/** Optional bounded integer (e.g. ages). */
const optionalBoundedAge = z.preprocess(
  emptyToUndefined,
  z.coerce
    .number()
    .int('Whole number')
    .min(18, 'Must be 18 or older')
    .max(120, 'Invalid age')
    .optional(),
)

/**
 * Schema for a single debt entry. Mirrors `DebtEntry` in fna_input.py.
 */
export const debtEntrySchema = z.object({
  debtName: z.string().min(1, 'Required').max(200),
  currentBalance: z.preprocess(
    emptyToUndefined,
    z.coerce.number().nonnegative('Cannot be negative').default(0),
  ),
  interestRate: z.preprocess(
    emptyToUndefined,
    z.coerce
      .number()
      .min(0, 'Cannot be negative')
      .max(100, 'Must be 0-100')
      .optional(),
  ),
  minimumPayment: z.preprocess(
    emptyToUndefined,
    z.coerce.number().nonnegative('Cannot be negative').optional(),
  ),
  debtType: z
    .string()
    .max(200)
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
})

/**
 * Top-level schema for the FNA profile form. Validation is enforced both
 * during inline editing (RHF + zodResolver) and at final submit (full schema
 * runs the `.superRefine` block below).
 */
export const fnaFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200),
    age: z.preprocess(
      emptyToUndefined,
      z.coerce
        .number({ error: 'Age is required' })
        .int('Whole number')
        .min(18, 'Must be 18 or older')
        .max(120, 'Invalid age'),
    ),
    gender: z
      .string()
      .max(30)
      .optional()
      .transform((value) => (value === '' ? undefined : value)),

    withSpouse: optionalYesNo,
    spouseName: z
      .string()
      .max(200)
      .optional()
      .transform((value) => (value === '' ? undefined : value)),
    spouseGender: z
      .string()
      .max(30)
      .optional()
      .transform((value) => (value === '' ? undefined : value)),
    spouseAge: optionalBoundedAge,
    occupation: z
      .string()
      .max(200)
      .optional()
      .transform((value) => (value === '' ? undefined : value)),
    spouseOccupation: z
      .string()
      .max(200)
      .optional()
      .transform((value) => (value === '' ? undefined : value)),

    annualIncome: optionalNonNegativeNumberDefault0,
    spouseAnnualIncome: optionalNonNegativeNumberDefault0,
    otherMonthlyIncome: optionalNonNegativeNumber,
    spouseOtherMonthlyIncome: optionalNonNegativeNumber,
    otherIncomeSource: optionalShortText,
    spouseOtherIncomeSource: optionalShortText,

    retireAge: optionalBoundedAge,
    retirementIncome: optionalMoreSameLess,
    additionalRetireIncome: optionalYesNo,
    sourceOfIncome: optionalShortText,
    monthlyAmount: optionalNonNegativeNumber,
    previousSavings: optionalYesNo,
    rolledOver: optionalYesNo,
    totalRetirementSavings: optionalNonNegativeNumber,
    monthlyRetirementContribution: optionalNonNegativeNumber,
    clientMonthlyContribution: optionalNonNegativeNumber,
    spouseMonthlyContribution: optionalNonNegativeNumber,
    clientEmployerContribution: optionalNonNegativeNumber,
    spouseEmployerContribution: optionalNonNegativeNumber,
    totalNonRetirementSavings: optionalNonNegativeNumber,
    clientRetirement: optionalNonNegativeNumber,
    spouseRetirement: optionalNonNegativeNumber,
    jointRetirement: optionalNonNegativeNumber,
    otherAssets: optionalNonNegativeNumber,
    clientNonRetirementSavings: optionalNonNegativeNumber,
    spouseNonRetirementSavings: optionalNonNegativeNumber,
    jointNonRetirementSavings: optionalNonNegativeNumber,
    clientMonthlySavings: optionalNonNegativeNumber,
    spouseMonthlySavings: optionalNonNegativeNumber,
    jointMonthlySavings: optionalNonNegativeNumber,

    lifeInsurance: optionalYesNo,
    insuredAmount: optionalNonNegativeNumber,

    estateToHeirs: optionalYesNo,
    amountEstate: optionalNonNegativeNumber,
    will: optionalYesNo,

    hasDebts: optionalYesNo,
    debts: z.array(debtEntrySchema).optional(),

    children: optionalYesNo,
    numChildren: z.preprocess(
      emptyToUndefined,
      z.coerce.number().int('Whole number').min(0, 'Cannot be negative').max(20, 'Too many').optional(),
    ),
  })
  .superRefine((data, ctx) => {
    if (data.retireAge != null && data.age != null && data.retireAge <= data.age) {
      ctx.addIssue({
        code: 'custom',
        path: ['retireAge'],
        message: 'Retirement age must be greater than current age',
      })
    }
    if (data.withSpouse === 'yes' && !data.spouseName) {
      ctx.addIssue({
        code: 'custom',
        path: ['spouseName'],
        message: 'Spouse name is required',
      })
    }
    if (data.hasDebts === 'yes' && (!data.debts || data.debts.length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['debts'],
        message: 'Add at least one debt or change the answer to "no"',
      })
    }
  })

export type FnaFormValues = z.infer<typeof fnaFormSchema>

/**
 * Input-side type (pre-parse). Useful for `useForm<FnaFormInput, _, FnaFormValues>`
 * because text inputs may carry strings before Zod coerces them to numbers.
 */
export type FnaFormInput = z.input<typeof fnaFormSchema>

/** Field name union, useful for narrow typing of step field lists. */
export type FnaFieldName = keyof FnaFormValues

/** All form steps and the field names each step is responsible for. */
export interface ProfileStepDef {
  id: string
  label: string
  fields: ReadonlyArray<FnaFieldName>
}

export const PROFILE_STEPS: ReadonlyArray<ProfileStepDef> = [
  {
    id: 'personal',
    label: 'Personal',
    fields: [
      'name',
      'age',
      'gender',
      'withSpouse',
      'spouseName',
      'spouseAge',
      'spouseGender',
      'children',
      'numChildren',
    ],
  },
  {
    id: 'income',
    label: 'Income',
    fields: [
      'annualIncome',
      'spouseAnnualIncome',
      'otherMonthlyIncome',
      'otherIncomeSource',
      'spouseOtherMonthlyIncome',
      'spouseOtherIncomeSource',
      'occupation',
      'spouseOccupation',
    ],
  },
  {
    id: 'retirement',
    label: 'Retirement',
    fields: [
      'retireAge',
      'retirementIncome',
      'additionalRetireIncome',
      'sourceOfIncome',
      'monthlyAmount',
      'previousSavings',
      'rolledOver',
      'totalRetirementSavings',
      'monthlyRetirementContribution',
      'clientMonthlyContribution',
      'spouseMonthlyContribution',
      'clientEmployerContribution',
      'spouseEmployerContribution',
    ],
  },
  {
    id: 'savings',
    label: 'Savings & Assets',
    fields: [
      'totalNonRetirementSavings',
      'clientRetirement',
      'spouseRetirement',
      'jointRetirement',
      'otherAssets',
      'clientNonRetirementSavings',
      'spouseNonRetirementSavings',
      'jointNonRetirementSavings',
      'clientMonthlySavings',
      'spouseMonthlySavings',
      'jointMonthlySavings',
    ],
  },
  {
    id: 'insurance',
    label: 'Insurance',
    fields: ['lifeInsurance', 'insuredAmount'],
  },
  {
    id: 'estate',
    label: 'Estate & Legacy',
    fields: ['estateToHeirs', 'amountEstate', 'will'],
  },
  {
    id: 'debts',
    label: 'Debts',
    fields: ['hasDebts', 'debts'],
  },
  {
    id: 'review',
    label: 'Review',
    fields: [],
  },
]
